import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServicePage from "@/pages/ServicePage";
import Industries from "@/pages/Industries";
import Careers from "@/pages/Careers";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { SERVICES } from "@/data/site";

const SEO_MAP = {
  "/": ["Daksh Detective & Security Services Pvt. Ltd. | Manpower Outsourcing & Security Services in Chandigarh", "Daksh DDSS provides manpower outsourcing, security guards, facility management, sanitation, warehousing and event security in Chandigarh, Mohali & Panchkula. Trusted since 2003."],
  "/about": ["About Us | Daksh DDSS — Manpower & Security Company in Chandigarh Since 2003", "Incorporated on 07 February 2003, Daksh Detective and Security Services Pvt. Ltd. is a Chandigarh-based manpower outsourcing, security and facility services company led by MD Arjun Singh Chauhan."],
  "/services": ["Our Services | Manpower, Security, Facility Management, Sanitation & Warehousing — Daksh DDSS", "Explore Daksh DDSS services: manpower outsourcing, security manpower, facility management, sanitation & cleaning, warehousing management and event security in Chandigarh."],
  "/manpower": ["Manpower Outsourcing Services in Chandigarh | Skilled & Unskilled Workforce — Daksh DDSS", "Flexible manpower outsourcing for offices, institutions and industries — office support staff, technicians, helpers, supervisors and project-based workforce in Chandigarh."],
  "/security-services": ["Security Guard Services in Chandigarh, Mohali & Panchkula | Daksh Detective & Security Services", "Trained security guards and supervisors for commercial, institutional, industrial and residential premises in Chandigarh. Security manpower since 2003."],
  "/facility-management": ["Facility Management Services in Chandigarh | Housekeeping & Support Staff — Daksh DDSS", "Operational manpower and facility management support — housekeeping, technical staff, pantry, utility and maintenance teams for offices and institutions."],
  "/sanitation": ["Sanitation & Cleaning Services in Chandigarh | Housekeeping Manpower — Daksh DDSS", "Trained sanitation and cleaning manpower for offices, hospitals, schools and commercial facilities — daily cleaning, deep cleaning and hygiene management."],
  "/warehousing": ["Warehouse Manpower & Management Services in Chandigarh | Daksh DDSS", "Warehouse workforce solutions — loaders, pickers, packers, forklift operators, inventory support and warehouse supervisors for logistics operations."],
  "/event-security": ["Event Security Services in Chandigarh | Crowd Management & Bouncers — Daksh DDSS", "Event security guards, crowd management, access control, VIP movement support and parking marshals for events and venues in Chandigarh."],
  "/industries": ["Industries We Serve | Manpower for Govt Departments, Hospitals, Malls & Factories — Daksh DDSS", "Daksh DDSS serves government departments, PSUs, hospitals, universities, corporate offices, hotels, malls, factories, warehouses and residential societies."],
  "/careers": ["Careers | Security Guard, Housekeeping & Facility Jobs in Chandigarh — Daksh DDSS", "Apply for security guard, supervisor, housekeeping, facility, warehouse and office support jobs at Daksh Detective and Security Services, Chandigarh."],
  "/contact": ["Contact Us | Daksh Detective & Security Services — Sector 17-B, Chandigarh", "Call +91 9878227336 or email ddsservicespvtltd@yahoo.in. Head office: SCO 10, 11, 12, Sector 17-B, Chandigarh. Manpower, security and facility services."],
  "/request-quote": ["Request a Quote | Manpower, Security & Facility Services Chandigarh — Daksh DDSS", "Share your manpower, security or facility requirement and get a structured proposal and quotation from Daksh DDSS, Chandigarh."],
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    const seo = SEO_MAP[pathname];
    if (seo) {
      document.title = seo[0];
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", seo[1]);
    }
  }, [pathname]);
  return null;
};

const SiteLayout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
    <WhatsAppFloat />
  </>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App font-body">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<SiteLayout><Home /></SiteLayout>} />
          <Route path="/about" element={<SiteLayout><About /></SiteLayout>} />
          <Route path="/services" element={<SiteLayout><Services /></SiteLayout>} />
          {SERVICES.map((s) => (
            <Route key={s.slug} path={s.path} element={<SiteLayout><ServicePage service={s} /></SiteLayout>} />
          ))}
          <Route path="/industries" element={<SiteLayout><Industries /></SiteLayout>} />
          <Route path="/careers" element={<SiteLayout><Careers /></SiteLayout>} />
          <Route path="/contact" element={<SiteLayout><Contact /></SiteLayout>} />
          <Route path="/request-quote" element={<SiteLayout><Quote /></SiteLayout>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
